import { ProjectHeader } from "@/components/projects/project-header";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingLabel } from "@/components/ui/loading-label";
import { LocalDate } from "@/components/ui/local-date";
import { Markdown } from "@/components/ui/markdown";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useDocumentTitle } from "@/lib/document-title";
import { workItemHref } from "@/lib/project-href";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/ideas/:id` — idea detail. Renders the description as Markdown
 * and, for open ideas, the convert-to-work-item and reject
 * actions. Converted ideas link to their promoted work item;
 * rejected ones can be reopened.
 *
 * The three mutations hit the new `/api/ideas/:id/{convert,
 * reject,reopen}` endpoints; each toasts + `router.invalidate()`s
 * so the card set re-renders for the new status.
 */

const statusVariant: Record<string, "secondary" | "success" | "outline" | "destructive"> = {
  open: "secondary",
  converted: "success",
  rejected: "outline",
};

type IdeaProjectRef = {
  id: string;
  slug: string;
  group: { path: string } | null;
};

type IdeaDetail = {
  id: string;
  title: string;
  description: string | null;
  status: "open" | "converted" | "rejected";
  rejectedReason: string | null;
  projectId: string | null;
  createdAt: string;
  author: { id: string; name: string | null };
  project: {
    id: string;
    name: string;
    slug: string;
    customer: { id: string; name: string } | null;
    group: { path: string } | null;
  } | null;
  workItem: {
    id: string;
    gitlabIssueId: string;
    globalId: number;
    title: string;
    project: IdeaProjectRef;
  } | null;
};

type IdeaDetailResponse = {
  idea: IdeaDetail;
  projects: Array<{ id: string; name: string; gitlabProjectId: string }>;
};

export const Route = createFileRoute("/_app/ideas/$id")({
  loader: ({ params }) => api.get<IdeaDetailResponse>(`/api/ideas/${params.id}`),
  component: IdeaDetailPage,
});

function IdeaDetailPage() {
  const { t } = useTranslation();
  const { idea, projects } = Route.useLoaderData();
  useDocumentTitle(idea.title);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {idea.project && <ProjectHeader project={idea.project} showTabs={false} />}
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <Link to="/ideas" className="text-xs text-primary hover:underline">
            {t("idea.backToIdeas")}
          </Link>
          <h1 className="font-heading text-2xl font-bold leading-tight">{idea.title}</h1>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant={statusVariant[idea.status] ?? "outline"}>
              {t(`idea.status.${idea.status}`)}
            </Badge>
            <span>{t("idea.by", { name: idea.author.name ?? "" })}</span>
            <span>·</span>
            <LocalDate value={idea.createdAt} />
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("idea.description")}</CardTitle>
        </CardHeader>
        <CardContent>
          {idea.description ? (
            <Markdown content={idea.description} gitlabUploadProjectId={idea.project?.id} />
          ) : (
            <p className="text-sm text-muted-foreground italic">{t("idea.noDescription")}</p>
          )}
        </CardContent>
      </Card>

      {idea.status === "converted" && idea.workItem && (
        <Card>
          <CardContent className="p-5 text-sm">
            {t("idea.promotedTo")}{" "}
            <AppLink
              href={workItemHref(idea.workItem.globalId)}
              className="text-primary hover:underline font-medium"
            >
              #{idea.workItem.globalId} {idea.workItem.title}
            </AppLink>
            .
          </CardContent>
        </Card>
      )}

      {idea.status === "rejected" && (
        <Card>
          <CardContent className="p-5 text-sm space-y-3">
            <div>
              <span className="text-muted-foreground">{t("idea.rejected")}</span>
              {idea.rejectedReason && <span className="ml-2">— {idea.rejectedReason}</span>}
            </div>
            <ReopenButton ideaId={idea.id} />
          </CardContent>
        </Card>
      )}

      {idea.status === "open" && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("idea.convert.title")}</CardTitle>
              <CardDescription>{t("idea.convert.hint")}</CardDescription>
            </CardHeader>
            <CardContent>
              <ConvertForm
                ideaId={idea.id}
                defaultProjectId={idea.projectId ?? ""}
                projects={projects}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base text-destructive">{t("idea.reject.title")}</CardTitle>
              <CardDescription>{t("idea.reject.hint")}</CardDescription>
            </CardHeader>
            <CardContent>
              <RejectForm ideaId={idea.id} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function ConvertForm({
  ideaId,
  defaultProjectId,
  projects,
}: {
  ideaId: string;
  defaultProjectId: string;
  projects: Array<{ id: string; name: string }>;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [projectId, setProjectId] = useState(defaultProjectId);
  const [issueType, setIssueType] = useState("feature");
  const convert = useApiMutation(
    () => api.post(`/api/ideas/${ideaId}/convert`, { projectId, issueType }),
    {
      onSuccess: () => {
        toast({ title: t("idea.convert.done"), variant: "success" });
        router.invalidate();
      },
      errorMessage: (err) =>
        err instanceof ApiError && typeof err.detail === "object" && err.detail !== null
          ? ((err.detail as { error?: string; detail?: string }).detail ??
            (err.detail as { error?: string }).error ??
            err.message)
          : err instanceof Error
            ? err.message
            : t("idea.convert.error"),
      onError: (msg) =>
        toast({ title: t("idea.convert.failed"), description: msg, variant: "destructive" }),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!projectId) {
      toast({ title: t("idea.convert.pickProject"), variant: "destructive" });
      return;
    }
    await convert.mutate();
  }

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5 flex-1 min-w-[200px]">
        <label htmlFor="projectId" className="text-xs font-medium leading-none">
          {t("idea.convert.project")}
        </label>
        <Select
          aria-label={t("idea.convert.project")}
          value={projectId}
          onChange={setProjectId}
          className="w-full"
          options={[
            { value: "", label: t("idea.convert.pick") },
            ...projects.map((p) => ({ value: p.id, label: p.name })),
          ]}
        />
      </div>
      <div className="space-y-1.5 min-w-[200px]">
        <label htmlFor="issueType" className="text-xs font-medium leading-none">
          {t("idea.convert.type")}
        </label>
        <Select
          aria-label={t("idea.convert.type")}
          value={issueType}
          onChange={setIssueType}
          className="w-full"
          options={[
            { value: "feature", label: t("idea.convert.feature") },
            { value: "bug", label: t("idea.convert.bug") },
            { value: "standalone", label: t("idea.convert.standalone") },
            { value: "hotfix", label: t("idea.convert.hotfix") },
          ]}
        />
      </div>
      <Button type="submit" disabled={convert.pending}>
        {convert.pending ? (
          <LoadingLabel text={t("idea.convert.converting")} />
        ) : (
          t("idea.convert.submit")
        )}
      </Button>
    </form>
  );
}

function RejectForm({ ideaId }: { ideaId: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const [reason, setReason] = useState("");
  const reject = useApiMutation(
    () => api.post(`/api/ideas/${ideaId}/reject`, { reason: reason.trim() || null }),
    {
      onSuccess: () => {
        toast({ title: t("idea.reject.done"), variant: "success" });
        router.invalidate();
      },
      errorMessage: (err) => (err instanceof Error ? err.message : t("idea.convert.error")),
      onError: (msg) =>
        toast({ title: t("idea.reject.failed"), description: msg, variant: "destructive" }),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await reject.mutate();
  }

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
      <div className="space-y-1.5 flex-1 min-w-[200px]">
        <label htmlFor="reason" className="text-xs font-medium leading-none">
          {t("idea.reject.reason")}{" "}
          <span className="text-muted-foreground">{t("idea.reject.optional")}</span>
        </label>
        <input
          id="reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t("idea.reject.placeholder")}
          className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <Button type="submit" variant="destructive" disabled={reject.pending}>
        {reject.pending ? (
          <LoadingLabel text={t("idea.reject.rejecting")} />
        ) : (
          t("idea.reject.submit")
        )}
      </Button>
    </form>
  );
}

function ReopenButton({ ideaId }: { ideaId: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const reopen = useApiMutation(() => api.post(`/api/ideas/${ideaId}/reopen`, {}), {
    onSuccess: () => {
      toast({ title: t("idea.reopen.done"), variant: "success" });
      router.invalidate();
    },
    errorMessage: (err) => (err instanceof Error ? err.message : t("idea.convert.error")),
    onError: (msg) =>
      toast({ title: t("idea.reopen.failed"), description: msg, variant: "destructive" }),
  });

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={reopen.pending}
      onClick={() => reopen.mutate()}
    >
      {reopen.pending ? (
        <LoadingLabel text={t("idea.reopen.reopening")} />
      ) : (
        t("idea.reopen.submit")
      )}
    </Button>
  );
}
